import Axios from "axios";
import Qs from "qs";
import { decamelizeKeys } from "humps";
import { getAccessToken } from "utilities/access-token";
import { isValid, parseISO } from "date-fns";
import { getClientId } from "./client-id";
import { captureException } from "@sentry/react";

export const BASE_URL = "/api";

// Dates formats the backend might send.
export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
export const AUTO_DATE_IGNORED_KEYS = ["tag"];

const languageIsoCodesTransformations: Record<string, string> = {
    zhcn: "zh-cn",
    zhtw: "zh-tw",
};

export const transformLanguageIsoCode = (languageIsoCode: string): string => {
    const lowerCaseIsoCode = languageIsoCode.toLowerCase();
    return languageIsoCodesTransformations[lowerCaseIsoCode] ?? lowerCaseIsoCode;
};

const axios = Axios.create({
    baseURL: BASE_URL,
    paramsSerializer: (parameters) =>
        Qs.stringify(decamelizeKeys(parameters), {
            arrayFormat: "repeat",
            skipNulls: true,
        }),
    transformResponse: (data, headers) => {
        try {
            return JSON.parse(data, (key, value) => {
                try {
                    // Attempt to transform strings to dates.
                    if (
                        typeof value === "string" &&
                        DATE_PATTERN.test(value) &&
                        !AUTO_DATE_IGNORED_KEYS.includes(key)
                    ) {
                        try {
                            const date = parseISO(value);
                            return isValid(date)
                                ? parseISO(
                                      value.replace(
                                          /([+-])(\d{2}:\d{2})/,
                                          (_, sign, offset) => `${sign === "+" ? "-" : "+"}${offset}`
                                      )
                                  )
                                : value;
                        } catch {
                            return value;
                        }
                    }

                    if ((key === "interactionType" || key === "topInteractionType") && typeof value === "string") {
                        return value.toUpperCase().replaceAll("_", " ");
                    }

                    if ((key === "interactionType" || key === "interactionTypes") && Array.isArray(value)) {
                        return value.map((interaction: string) => interaction.toUpperCase().replaceAll("_", " "));
                    }

                    if (key === "language" && typeof value === "string") {
                        return languageIsoCodesTransformations[value] ?? value;
                    }
                    if (key === "languageIsoCode" && typeof value === "string") {
                        return transformLanguageIsoCode(value);
                    }

                    if ((key === "languageIsoCode" || key === "languageIsoCodes") && Array.isArray(value)) {
                        return value.map((languageIsoCode: string) => transformLanguageIsoCode(languageIsoCode));
                    }

                    return value;
                } catch (error) {
                    captureException(error);
                    // If there was an error while transforming a value, return the value as JSON.parse has parsed it.
                    return value;
                }
            });
        } catch (error) {
            captureException(error);
            // If there was an error while parsing the JSON, return JSON string.
            return data;
        }
    },
});

/**
 * Put the access token in a header.
 */
export const createAuthorisationHeader = () => `Bearer ${getAccessToken()}`;

axios.interceptors.request.use((request) => {
    const clientId = getClientId();

    // Set headers to an empty object if the headers property is null-ish.
    if (!request.headers) {
        request.headers = {};
    }

    if (clientId !== null) {
        request.headers["WatchTower-Client-ID"] = clientId;
    }
    request.headers["Authorization"] = createAuthorisationHeader();
    return request;
});

export { axios };

export default Axios;
